import React, { useState, useEffect } from 'react';
import { BarChart3, Plus, Edit2, Trash2, User, Calendar, Loader2, Play } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { teamsAPI, rosterAPI, calendarAPI, trainingAPI, BACKEND_URL } from '../services/api';
import { format, parseISO, subMonths, eachDayOfInterval, subDays, startOfToday } from 'date-fns';
import TeamGate from '../components/TeamGate';
import VideoPlayer from '../components/VideoPlayer';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../context/TeamContext';
import PremiumGateOverlay from '../components/PremiumGateOverlay';

// American Football stat fields (matching video clip stats)
const OFFENSE_STATS = ['passing', 'rushing', 'receiving', 'touchdowns'];
const DEFENSE_STATS = ['tackles', 'sacks', 'interceptions', 'pass_deflections', 'forced_fumbles'];

// American Football position groups
const OFFENSIVE_POSITIONS = ['QB', 'RB', 'WR', 'TE', 'OL', 'C', 'G', 'T', 'FB'];
const DEFENSIVE_POSITIONS = ['DL', 'LB', 'DB', 'CB', 'S', 'DE', 'DT', 'NT', 'FS', 'SS', 'MLB', 'OLB'];

const formatStatName = (stat, t) => {
  if (t) return t(`playerStats.stats.${stat}`);
  return stat.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const PlayerStats = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { plan } = useTeam();
  const isFreePlan = plan !== 'premium';
  const [players, setPlayers] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null); // When null, show leaderboard

  // Derive selectedPlayer from aggregatedData to ensure stats update when filters change
  // MOVED: const selectedPlayer = selectedPlayerId ? aggregatedData.find(p => p.user_id === selectedPlayerId) : null;
  const [allVideos, setAllVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'touchdowns', direction: 'desc' });
  const [defenseSortConfig, setDefenseSortConfig] = useState({ key: 'tackles', direction: 'desc' });
  const [season, setSeason] = useState('2025-2026');
  const [playingVideo, setPlayingVideo] = useState(null);
  const [selectedStatType, setSelectedStatType] = useState(null); // For filtering videos by stat
  const [timeFilter, setTimeFilter] = useState('season'); // 'month', '3months', 'season'

  useEffect(() => {
    if (user?.team_id) {
      loadData();
    }
  }, [user?.team_id]);

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[calc(100vh-100px)] text-center px-4">
        <BarChart3 className="w-16 h-16 text-gray-700 mb-4" />
        <h2 className="text-2xl font-bold text-white mb-2">{t('nav.stats', 'İstatistikler')}</h2>
        <p className="text-gray-400 mb-6 max-w-sm">Detaylı oyuncu ve takım istatistiklerini görüntüleyebilmek için giriş yapmanız ve bir takıma üye olmanız gerekmektedir.</p>
      </div>
    );
  }

  const loadData = async () => {
    try {
      setLoading(true);
      const [rosterRes, videosRes] = await Promise.all([
        rosterAPI.getRoster(user.team_id),
        trainingAPI.listTeamVideos(user.team_id)
      ]);

      setPlayers(rosterRes.data.filter(m => m.role === 'player'));
      setAllVideos(videosRes.data);
    } catch (error) {
      console.error('Failed to load stats data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter videos based on time selection
  const filteredVideos = allVideos.filter(video => {
    if (timeFilter === 'season') return true;
    const date = parseISO(video.created_at);
    const now = new Date();
    if (timeFilter === 'month') return date >= subMonths(now, 1);
    if (timeFilter === '3months') return date >= subMonths(now, 3);
    return true;
  });

  // Helper to find peer averages (excluding zeros)
  // Logic: Find all players in same position group, aggregate their stats for specific stat type.
  // 1. Get unique dates from training sessions only
  const uniqueDates = React.useMemo(() => {
    return Array.from(new Set(filteredVideos.map(v => {
      const d = v.session_date || v.created_at;
      return format(parseISO(d), 'yyyy-MM-dd');
    }))).sort();
  }, [filteredVideos]);

  // Helper to find peer averages (excluding zeros)
  const calculatePeerAverageCurve = (statType, positionCategory, allHistoryMaps) => {
    // Find all players in this category
    const peers = players.filter(p => {
      // Determine p's category
      const pPos = p.positions || [];
      if (positionCategory === 'receivers') return pPos.includes('WR') || pPos.includes('TE');
      if (positionCategory === 'backfield') return pPos.includes('RB') || pPos.includes('FB');
      if (positionCategory === 'line') return pPos.some(pos => ['OL', 'C', 'G', 'T'].includes(pos));
      if (positionCategory === 'qb') return pPos.includes('QB');
      // Defense
      if (positionCategory === 'defensive_line') return pPos.some(pos => ['DL', 'DE', 'DT', 'NT'].includes(pos));
      if (positionCategory === 'linebackers') return pPos.some(pos => ['LB', 'MLB', 'OLB'].includes(pos));
      if (positionCategory === 'secondary') return pPos.some(pos => ['DB', 'CB', 'S', 'FS', 'SS'].includes(pos));
      return false;
    });

    const averageCurveMap = {};

    uniqueDates.forEach(dateKey => {
      let cumulativeTotal = 0;
      let count = 0;

      peers.forEach(peer => {
        const peerHistory = allHistoryMaps[peer.user_id]?.[statType];
        if (peerHistory) {
          const relevantPoint = peerHistory.find(h => h.timestamp === new Date(dateKey).getTime());
          const val = relevantPoint ? relevantPoint.value : 0;

          if (val > 0) {
            cumulativeTotal += val;
            count++;
          }
        }
      });

      averageCurveMap[dateKey] = count > 0 ? (cumulativeTotal / count) : 0;
    });

    return averageCurveMap;
  };
  // Aggregate stats for all players with FILTER
  const aggregatedData = players.map(player => {
    const playerEntries = [];
    filteredVideos.forEach(video => {
      const videoStats = video.play_data?.player_stats || [];
      const entries = videoStats.filter(s => s.player_id === player.user_id);
      entries.forEach(e => {
        playerEntries.push({
          date: video.session_date || video.created_at,
          stat_type: e.stat_type,
          value: e.value,
          video_title: video.title
        });
      });
    });

    const totals = {};
    const historyMap = {};

    playerEntries.forEach(entry => {
      totals[entry.stat_type] = (totals[entry.stat_type] || 0) + entry.value;

      const dateKey = format(parseISO(entry.date), 'yyyy-MM-dd');
      if (!historyMap[entry.stat_type]) historyMap[entry.stat_type] = {};

      if (!historyMap[entry.stat_type][dateKey]) {
        historyMap[entry.stat_type][dateKey] = {
          date: format(parseISO(entry.date), 'MMM d'),
          value: 0,
          timestamp: new Date(dateKey).getTime()
        };
      }
      historyMap[entry.stat_type][dateKey].value += entry.value;
    });

    // Convert map to array and compute cumulative, ensuring all unique dates are present
    const finalHistoryMap = {};
    Object.keys(historyMap).forEach(type => {
      let cumulative = 0;
      finalHistoryMap[type] = uniqueDates.map(dateKey => {
        const point = historyMap[type][dateKey];
        if (point) {
          cumulative += point.value;
        }
        return {
          date: format(parseISO(dateKey), 'MMM d'),
          value: point ? point.value : 0,
          timestamp: new Date(dateKey).getTime(),
          cumulative
        };
      });
    });

    return {
      ...player,
      totals,
      historyMap: finalHistoryMap, // Stores this player's history
      user_id: player.user_id, // Ensure ID is avail
      positions: player.positions,
      hasOffense: player.positions?.some(pos => OFFENSIVE_POSITIONS.includes(pos)) || false,
      hasDefense: player.positions?.some(pos => DEFENSIVE_POSITIONS.includes(pos)) || false,
      totalImpact: Object.values(totals).reduce((a, b) => a + b, 0)
    };
  });

  // Derived state moved here to access aggregatedData
  const selectedPlayer = selectedPlayerId ? aggregatedData.find(p => p.user_id === selectedPlayerId) : null;

  // Pre-calculate all histories for peer averaging
  const globalHistoryMaps = {};
  aggregatedData.forEach(p => {
    globalHistoryMaps[p.user_id] = p.historyMap;
  });

  const handleSort = (key, isDefense = false) => {
    const currentConfig = isDefense ? defenseSortConfig : sortConfig;
    let direction = 'desc';

    if (currentConfig.key === key && currentConfig.direction === 'desc') {
      direction = 'asc';
    }

    if (isDefense) {
      setDefenseSortConfig({ key, direction });
    } else {
      setSortConfig({ key, direction });
    }
  };

  const getSortIndicator = (key, isDefense = false) => {
    const currentConfig = isDefense ? defenseSortConfig : sortConfig;
    if (currentConfig.key !== key) return null;
    return currentConfig.direction === 'desc' ? ' ↓' : ' ↑';
  };

  const groupLeaders = (leaders, categories) => {
    const groups = {};
    categories.forEach(cat => groups[cat.label] = []);

    leaders.forEach(player => {
      const category = categories.find(cat =>
        cat.positions.some(pos => (player.positions || []).includes(pos))
      ) || categories[categories.length - 1];
      groups[category.label].push(player);
    });

    return Object.entries(groups).filter(([_, players]) => players.length > 0);
  };

  // Updated Groupings: Combined WR/TE
  const OFFENSE_CATS = [
    { label: t('playerStats.categories.quarterbacks'), positions: ['QB'] },
    { label: t('playerStats.categories.receivers'), positions: ['WR', 'TE'] }, // Combined
    { label: t('playerStats.categories.runningBacks'), positions: ['RB', 'FB', 'HB'] },
    { label: t('playerStats.categories.offensiveLine'), positions: ['OL', 'C', 'G', 'T', 'OG', 'OT'] },
    { label: t('playerStats.categories.other'), positions: [] }
  ];

  const DEFENSE_CATS = [
    { label: t('playerStats.categories.defensiveLine'), positions: ['DL', 'DE', 'DT', 'NT'] },
    { label: t('playerStats.categories.linebackers'), positions: ['LB', 'MLB', 'OLB'] },
    { label: t('playerStats.categories.secondary'), positions: ['DB', 'CB', 'S', 'FS', 'SS'] },
    { label: t('playerStats.categories.other'), positions: [] }
  ];

  const offenseLeaders = aggregatedData
    .filter(p => p.hasOffense || (!p.hasOffense && !p.hasDefense))
    .sort((a, b) => {
      const valA = a.totals[sortConfig.key] || 0;
      const valB = b.totals[sortConfig.key] || 0;

      if (valA === valB) {
        return b.totalImpact - a.totalImpact; // Tie-breaker
      }

      return sortConfig.direction === 'desc' ? valB - valA : valA - valB;
    });

  const defenseLeaders = aggregatedData
    .filter(p => p.hasDefense)
    .sort((a, b) => {
      const valA = a.totals[defenseSortConfig.key] || 0;
      const valB = b.totals[defenseSortConfig.key] || 0;

      if (valA === valB) {
        return b.totalImpact - a.totalImpact; // Tie-breaker
      }

      return defenseSortConfig.direction === 'desc' ? valB - valA : valA - valB;
    });

  const offensePositionalLeaders = groupLeaders(offenseLeaders, OFFENSE_CATS);
  const defensePositionalLeaders = groupLeaders(defenseLeaders, DEFENSE_CATS);

  const getPlayerCategory = (player) => {
    const pos = player.positions || [];
    // Offense Checks
    if (pos.includes('QB')) return 'qb';
    if (pos.includes('WR') || pos.includes('TE')) return 'receivers';
    if (pos.includes('RB') || pos.includes('FB')) return 'backfield';
    if (pos.some(p => ['OL', 'C', 'G', 'T'].includes(p))) return 'line';

    // Defense Checks
    if (pos.some(p => ['DL', 'DE', 'DT'].includes(p))) return 'defensive_line';
    if (pos.some(p => ['LB', 'MLB', 'OLB'].includes(p))) return 'linebackers';
    if (pos.some(p => ['CB', 'S', 'FS', 'SS', 'DB'].includes(p))) return 'secondary';

    return 'other';
  };

  if (loading) {
    return (
      <TeamGate title="Player Stats">
        <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
          <p className="text-gray-400 animate-pulse">{t('playerStats.scanning')}</p>
        </div>
      </TeamGate>
    );
  }

  return (
    <TeamGate title="Player Stats">
      <div className="relative min-h-[80vh]">
        {isFreePlan && (
          <div className="absolute inset-0 z-50 bg-[#0a0a0b]/40 backdrop-blur-[2px] pointer-events-auto">
            <PremiumGateOverlay onClose={() => window.history.back()} />
          </div>
        )}

        <div className={`p-4 md:p-8 ${isFreePlan ? 'opacity-20 pointer-events-none blur-sm' : ''}`}>
          {!selectedPlayerId ? (
            /* LEADERBOARD VIEW */
            <div className="space-y-6 md:space-y-8 animate-in fade-in duration-500">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <h1 className="text-xl md:text-3xl font-black text-white flex items-center gap-2 md:gap-3">
                    <BarChart3 className="w-6 h-6 md:w-8 md:h-8 text-green-500" />
                    {t('playerStats.leaderboard')}
                  </h1>
                  <p className="text-xs md:text-base text-gray-500 font-medium">{t('playerStats.leaderboardDesc', { count: filteredVideos.length })}</p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  {/* Time Filter */}
                  <div className="flex bg-[#1a1a1b] rounded-lg p-1 border border-gray-800 self-start sm:self-auto w-full sm:w-auto overflow-x-auto scollbar-hide">
                    <button onClick={() => setTimeFilter('month')} className={`flex-1 whitespace-nowrap px-3 py-1.5 md:py-1 text-xs font-bold rounded-md transition-colors ${timeFilter === 'month' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-white'}`}>{t('playerStats.last30Days')}</button>
                    <button onClick={() => setTimeFilter('3months')} className={`flex-1 whitespace-nowrap px-3 py-1.5 md:py-1 text-xs font-bold rounded-md transition-colors ${timeFilter === '3months' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-white'}`}>{t('playerStats.last3Months')}</button>
                    <button onClick={() => setTimeFilter('season')} className={`flex-1 whitespace-nowrap px-3 py-1.5 md:py-1 text-xs font-bold rounded-md transition-colors ${timeFilter === 'season' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-white'}`}>{t('playerStats.thisSeason')}</button>
                  </div>

                  <div className="flex items-center gap-2 self-start sm:self-auto">
                    <span className="text-xs font-black text-gray-600 uppercase tracking-widest sm:pl-2">{t('playerStats.season')}</span>
                    <Select value={season} onValueChange={setSeason}>
                      <SelectTrigger className="w-[120px] md:w-[140px] bg-[#1a1a1b] border-gray-800 text-white h-9 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1a1a1b] border-gray-800">
                        <SelectItem value="2025-2026">2025-2026</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Offense Leaders */}
                <Card className="bg-[#1a1a1b] border-gray-800 overflow-hidden">
                  <div className="h-1 bg-blue-600"></div>
                  <CardHeader className="bg-[#0a0a0b]/50 border-b border-gray-800/50">
                    <CardTitle className="text-sm font-black text-blue-400 uppercase tracking-widest flex items-center justify-between">
                      <span>{t('playerStats.offensiveLeaders')}</span>
                      <Badge className="bg-blue-600/10 text-blue-500 border-none text-[10px]">{t('playerStats.focus', { stat: formatStatName(sortConfig.key, t) })}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-gray-800 text-[10px] text-gray-500 uppercase font-black tracking-tighter cursor-pointer">
                            <th className="px-6 py-4">{t('playerStats.player')}</th>
                            <th className="px-4 py-4 text-center hover:text-white transition-colors" onClick={() => handleSort('passing')}>
                              PASS{getSortIndicator('passing')}
                            </th>
                            <th className="px-4 py-4 text-center hover:text-white transition-colors" onClick={() => handleSort('rushing')}>
                              RUSH{getSortIndicator('rushing')}
                            </th>
                            <th className="px-4 py-4 text-center hover:text-white transition-colors" onClick={() => handleSort('receiving')}>
                              REC{getSortIndicator('receiving')}
                            </th>
                            <th className="px-4 py-4 text-right pr-6 hover:text-white transition-colors" onClick={() => handleSort('touchdowns')}>
                              TD{getSortIndicator('touchdowns')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {offensePositionalLeaders.map(([groupName, players]) => (
                            <React.Fragment key={groupName}>
                              <tr className="bg-[#0a0a0b]/40 border-b border-gray-800/10">
                                <td colSpan={5} className="px-6 py-1">
                                  <span className="text-[9px] font-bold text-gray-600 uppercase tracking-wider">{groupName}</span>
                                </td>
                              </tr>
                              {players.map((player, idx) => (
                                <tr
                                  key={player.user_id}
                                  onClick={() => setSelectedPlayerId(player.user_id)}
                                  className="group hover:bg-blue-600/5 cursor-pointer transition-colors border-b border-gray-800/20"
                                >
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <span className={`text-xs font-black w-4 ${idx < 3 && player.totalImpact > 0 ? 'text-blue-500' : 'text-gray-700'}`}>{idx + 1}</span>
                                      <div>
                                        <p className="text-sm font-bold text-white group-hover:text-blue-400 transition-colors">{player.username}</p>
                                        <p className="text-[10px] text-gray-600 font-bold uppercase">{player.positions?.join(', ') || t('playerStats.player')}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className={`px-4 py-4 text-center text-sm font-medium tabular-nums ${sortConfig.key === 'passing' ? 'text-white' : 'text-gray-400'}`}>{player.totals.passing || 0}</td>
                                  <td className={`px-4 py-4 text-center text-sm font-medium tabular-nums ${sortConfig.key === 'rushing' ? 'text-white' : 'text-gray-400'}`}>{player.totals.rushing || 0}</td>
                                  <td className={`px-4 py-4 text-center text-sm font-medium tabular-nums ${sortConfig.key === 'receiving' ? 'text-white' : 'text-gray-400'}`}>{player.totals.receiving || 0}</td>
                                  <td className={`px-4 py-4 text-right pr-6 text-base font-black tabular-nums ${sortConfig.key === 'touchdowns' ? 'text-blue-400' : 'text-gray-500'}`}>{player.totals.touchdowns || 0}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card List View */}
                    <div className="md:hidden flex flex-col divide-y divide-gray-800">
                      {/* Mobile Sort Controls */}
                      <div className="flex items-center gap-2 p-3 bg-[#0a0a0b]/40 overflow-x-auto scrollbar-hide text-[10px] font-black tracking-widest text-gray-500 uppercase">
                        <span className="shrink-0 mr-2">{t('playerStats.sort')}:</span>
                        {[
                          { key: 'passing', label: 'PASS' },
                          { key: 'rushing', label: 'RUSH' },
                          { key: 'receiving', label: 'REC' },
                          { key: 'touchdowns', label: 'TD' }
                        ].map(stat => (
                          <button
                            key={stat.key}
                            onClick={() => handleSort(stat.key)}
                            className={`px-3 py-1.5 rounded-full shrink-0 transition-colors ${sortConfig.key === stat.key ? 'bg-blue-600/20 text-blue-400' : 'bg-gray-800 text-gray-400'}`}
                          >
                            {stat.label} {sortConfig.key === stat.key && (sortConfig.direction === 'desc' ? '↓' : '↑')}
                          </button>
                        ))}
                      </div>

                      {offensePositionalLeaders.map(([groupName, players]) => (
                        <React.Fragment key={groupName}>
                          <div className="bg-[#0a0a0b]/40 border-b border-gray-800/10 px-4 py-1.5">
                            <span className="text-[9px] font-bold text-gray-600 uppercase tracking-wider">{groupName}</span>
                          </div>
                          {players.map((player, idx) => (
                            <div
                              key={player.user_id}
                              onClick={() => setSelectedPlayerId(player.user_id)}
                              className="group p-4 flex flex-col gap-3 hover:bg-blue-600/5 cursor-pointer transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black ${idx < 3 && player.totalImpact > 0 ? 'bg-blue-600/20 text-blue-500' : 'bg-gray-800 text-gray-400'}`}>
                                    {idx + 1}
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-base font-bold text-white group-hover:text-blue-400 transition-colors">{player.username}</span>
                                    <span className="text-[10px] text-gray-600 font-bold uppercase">{player.positions?.join(', ') || t('playerStats.player')}</span>
                                  </div>
                                </div>

                                {/* Highlight the primary sorted stat distinctly */}
                                <div className="flex flex-col items-end">
                                  <span className="text-2xl font-black text-blue-400 tabular-nums leading-none">
                                    {player.totals[sortConfig.key] || 0}
                                  </span>
                                  <span className="text-[9px] font-black text-blue-600/80 uppercase tracking-widest mt-1">
                                    {sortConfig.key === 'touchdowns' ? 'TD' : sortConfig.key}
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center gap-1.5 text-xs font-medium tabular-nums px-1 overflow-x-auto scrollbar-hide py-1">
                                {['passing', 'rushing', 'receiving', 'touchdowns'].map(stat => {
                                  if (stat === sortConfig.key) return null; // Already highlighted
                                  const label = stat === 'touchdowns' ? 'TD' : stat.substring(0, 3).toUpperCase();
                                  return (
                                    <div key={stat} className="flex items-center gap-1.5 bg-[#0a0a0b] border border-gray-800 px-2 py-1 rounded">
                                      <span className="text-[10px] font-bold text-gray-600">{label}:</span>
                                      <span className="text-gray-300">{player.totals[stat] || 0}</span>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          ))}
                        </React.Fragment>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Defense Leaders */}
                <Card className="bg-[#1a1a1b] border-gray-800 overflow-hidden">
                  <div className="h-1 bg-red-600"></div>
                  <CardHeader className="bg-[#0a0a0b]/50 border-b border-gray-800/50">
                    <CardTitle className="text-sm font-black text-red-400 uppercase tracking-widest flex items-center justify-between">
                      <span>{t('playerStats.defensiveLeaders')}</span>
                      <Badge className="bg-red-600/10 text-red-500 border-none text-[10px]">{t('playerStats.focus', { stat: formatStatName(defenseSortConfig.key, t) })}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    {/* Desktop Table View */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-left">
                        <thead>
                          <tr className="border-b border-gray-800 text-[10px] text-gray-500 uppercase font-black tracking-tighter cursor-pointer">
                            <th className="px-6 py-4">{t('playerStats.player')}</th>
                            <th className="px-4 py-4 text-center hover:text-white transition-colors" onClick={() => handleSort('tackles', true)}>
                              TCK{getSortIndicator('tackles', true)}
                            </th>
                            <th className="px-4 py-4 text-center hover:text-white transition-colors" onClick={() => handleSort('sacks', true)}>
                              SCK{getSortIndicator('sacks', true)}
                            </th>
                            <th className="px-4 py-4 text-center hover:text-white transition-colors" onClick={() => handleSort('interceptions', true)}>
                              INT{getSortIndicator('interceptions', true)}
                            </th>
                            <th className="px-4 py-4 text-right pr-6 hover:text-white transition-colors" onClick={() => handleSort('pass_deflections', true)}>
                              PDEF{getSortIndicator('pass_deflections', true)}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {defensePositionalLeaders.map(([groupName, players]) => (
                            <React.Fragment key={groupName}>
                              <tr className="bg-[#0a0a0b]/40 border-b border-gray-800/10">
                                <td colSpan={5} className="px-6 py-1">
                                  <span className="text-[9px] font-bold text-gray-600 uppercase tracking-wider">{groupName}</span>
                                </td>
                              </tr>
                              {players.map((player, idx) => (
                                <tr
                                  key={player.user_id}
                                  onClick={() => setSelectedPlayerId(player.user_id)}
                                  className="group hover:bg-red-600/5 cursor-pointer transition-colors border-b border-gray-800/20"
                                >
                                  <td className="px-6 py-4">
                                    <div className="flex items-center gap-3">
                                      <span className={`text-xs font-black w-4 ${idx < 3 && player.totalImpact > 0 ? 'text-red-500' : 'text-gray-700'}`}>{idx + 1}</span>
                                      <div>
                                        <p className="text-sm font-bold text-white group-hover:text-red-400 transition-colors">{player.username}</p>
                                        <p className="text-[10px] text-gray-600 font-bold uppercase">{player.positions?.join(', ') || t('playerStats.player')}</p>
                                      </div>
                                    </div>
                                  </td>
                                  <td className={`px-4 py-4 text-center text-sm font-medium tabular-nums ${defenseSortConfig.key === 'tackles' ? 'text-white' : 'text-gray-400'}`}>{player.totals.tackles || 0}</td>
                                  <td className={`px-4 py-4 text-center text-sm font-medium tabular-nums ${defenseSortConfig.key === 'sacks' ? 'text-white' : 'text-gray-400'}`}>{player.totals.sacks || 0}</td>
                                  <td className={`px-4 py-4 text-center text-sm font-medium tabular-nums ${defenseSortConfig.key === 'interceptions' ? 'text-white' : 'text-gray-400'}`}>{player.totals.interceptions || 0}</td>
                                  <td className={`px-4 py-4 text-right pr-6 text-sm font-bold tabular-nums ${defenseSortConfig.key === 'pass_deflections' ? 'text-red-400' : 'text-gray-500'}`}>{player.totals.pass_deflections || 0}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile Card List View for Defense */}
                    <div className="md:hidden flex flex-col divide-y divide-gray-800">
                      {/* Mobile Sort Controls */}
                      <div className="flex items-center gap-2 p-3 bg-[#0a0a0b]/40 overflow-x-auto scrollbar-hide text-[10px] font-black tracking-widest text-gray-500 uppercase">
                        <span className="shrink-0 mr-2">{t('playerStats.sort')}:</span>
                        {[
                          { key: 'tackles', label: 'TCK' },
                          { key: 'sacks', label: 'SCK' },
                          { key: 'interceptions', label: 'INT' },
                          { key: 'pass_deflections', label: 'PDEF' }
                        ].map(stat => (
                          <button
                            key={stat.key}
                            onClick={() => handleSort(stat.key, true)}
                            className={`px-3 py-1.5 rounded-full shrink-0 transition-colors ${defenseSortConfig.key === stat.key ? 'bg-red-600/20 text-red-400' : 'bg-gray-800 text-gray-400'}`}
                          >
                            {stat.label} {defenseSortConfig.key === stat.key && (defenseSortConfig.direction === 'desc' ? '↓' : '↑')}
                          </button>
                        ))}
                      </div>

                      {defensePositionalLeaders.map(([groupName, players]) => (
                        <React.Fragment key={groupName}>
                          <div className="bg-[#0a0a0b]/40 border-b border-gray-800/10 px-4 py-1.5">
                            <span className="text-[9px] font-bold text-gray-600 uppercase tracking-wider">{groupName}</span>
                          </div>
                          {players.map((player, idx) => (
                            <div
                              key={player.user_id}
                              onClick={() => setSelectedPlayerId(player.user_id)}
                              className="group p-4 flex flex-col gap-3 hover:bg-red-600/5 cursor-pointer transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black ${idx < 3 && player.totalImpact > 0 ? 'bg-red-600/20 text-red-500' : 'bg-gray-800 text-gray-400'}`}>
                                    {idx + 1}
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-base font-bold text-white group-hover:text-red-400 transition-colors">{player.username}</span>
                                    <span className="text-[10px] text-gray-600 font-bold uppercase">{player.positions?.join(', ') || t('playerStats.player')}</span>
                                  </div>
                                </div>

                                {/* Highlight the primary sorted stat distinctly */}
                                <div className="flex flex-col items-end">
                                  <span className="text-2xl font-black text-red-400 tabular-nums leading-none">
                                    {player.totals[defenseSortConfig.key] || 0}
                                  </span>
                                  <span className="text-[9px] font-black text-red-600/80 uppercase tracking-widest mt-1">
                                    {['tackles', 'sacks', 'interceptions', 'pass_deflections'].find(s => s === defenseSortConfig.key)?.substring(0, 4) || defenseSortConfig.key}
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center gap-1.5 text-xs font-medium tabular-nums px-1 overflow-x-auto scrollbar-hide py-1">
                                {['tackles', 'sacks', 'interceptions', 'pass_deflections'].map(stat => {
                                  if (stat === defenseSortConfig.key) return null; // Already highlighted
                                  const label = stat === 'tackles' ? 'TCK' : stat === 'sacks' ? 'SCK' : stat === 'interceptions' ? 'INT' : 'PDEF';
                                  return (
                                    <div key={stat} className="flex items-center gap-1.5 bg-[#0a0a0b] border border-gray-800 px-2 py-1 rounded">
                                      <span className="text-[10px] font-bold text-gray-600">{label}:</span>
                                      <span className="text-gray-300">{player.totals[stat] || 0}</span>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          ))}
                        </React.Fragment>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            /* PLAYER DETAIL VIEW */
            <div className="space-y-8 animate-in slide-in-from-right duration-500">
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  onClick={() => setSelectedPlayerId(null)}
                  className="text-gray-500 hover:text-white group flex items-center gap-2"
                >
                  <Plus className="w-4 h-4 rotate-45 group-hover:scale-110 transition-transform" />
                  Back to Leaderboard
                </Button>
                <Badge className="bg-blue-600/20 text-blue-400 border-blue-800 uppercase text-[10px] font-black tracking-widest">
                  Player Detailed Profile
                </Badge>
              </div>

              <div className="bg-[#1a1a1b] rounded-2xl border border-gray-800 p-6 md:p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/5 blur-[100px] -mr-32 -mt-32"></div>
                <div className="relative z-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-2xl font-black text-white shadow-xl shadow-blue-900/20 shrink-0">
                      {selectedPlayer.username[0].toUpperCase()}
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-4xl font-black text-white tracking-tighter leading-tight">{selectedPlayer.username}</h2>
                      <div className="flex flex-wrap items-center gap-2 md:gap-3 mt-1">
                        <span className="text-xs font-black text-blue-500 uppercase tracking-widest">{selectedPlayer.positions?.join(' / ') || t('playerStats.athlete')}</span>
                        <div className="h-1 w-1 rounded-full bg-gray-800"></div>
                        <span className="text-xs font-bold text-gray-500">{t('playerStats.globalScore', { score: selectedPlayer.totalImpact })}</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(selectedPlayer.totals).map(([stat, val]) => (
                      <div
                        key={stat}
                        onClick={() => setSelectedStatType(selectedStatType === stat ? null : stat)}
                        className={`px-4 py-2 rounded-xl border transition-all cursor-pointer ${selectedStatType === stat
                          ? 'bg-snapp-green/10 border-snapp-green shadow-snapp-green/20 shadow-lg scale-105'
                          : 'bg-[#0a0a0b] border-gray-800 hover:border-snapp-green/30 hover:bg-snapp-green/5'
                          }`}
                      >
                        <p className="text-[9px] text-gray-600 font-black uppercase tracking-widest">{formatStatName(stat, t)}</p>
                        <p className={`text-xl font-black tabular-nums ${selectedStatType === stat ? 'text-snapp-green' : 'text-white'
                          }`}>+{val}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {selectedStatType && (
                <div className="bg-snapp-green/5 border border-snapp-green/20 rounded-xl p-6">
                  <h3 className="text-sm font-black text-snapp-green uppercase tracking-widest mb-4 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    {t('playerStats.videosWithStat', { stat: formatStatName(selectedStatType, t) })}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredVideos
                      .filter(video => {
                        const stats = video.play_data?.player_stats || [];
                        return stats.some(s => s.player_id === selectedPlayer.user_id && s.stat_type === selectedStatType);
                      })
                      .map(video => (
                        <div
                          key={video.id}
                          onClick={() => setPlayingVideo(video)}
                          className="bg-[#1a1a1b] border border-gray-800 rounded-lg p-4 hover:border-snapp-green/50 hover:bg-snapp-green/5 transition-all cursor-pointer group flex items-center justify-between"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center group-hover:bg-snapp-green group-hover:text-black transition-colors">
                              <Play className="w-4 h-4 fill-current ml-0.5" />
                            </div>
                            <div>
                              <h4 className="text-white font-bold text-sm tracking-tight group-hover:text-snapp-green transition-colors">{video.title}</h4>
                              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">{format(parseISO(video.created_at), 'MMM d, yyyy')}</p>
                            </div>
                          </div>
                          <Badge className="bg-snapp-green/10 text-snapp-green border-snapp-green/30 text-[10px] h-5 px-1.5 font-black">
                            +{video.play_data?.player_stats?.find(s => s.player_id === selectedPlayer.user_id && s.stat_type === selectedStatType)?.value || 0}
                          </Badge>
                        </div>
                      ))}
                  </div>
                  {/* Empty State */}
                  {filteredVideos.filter(video => {
                    const stats = video.play_data?.player_stats || [];
                    return stats.some(s => s.player_id === selectedPlayer.user_id && s.stat_type === selectedStatType);
                  }).length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-gray-500 text-xs">{t('playerStats.noClips')}</p>
                      </div>
                    )}
                </div>
              )}

              {/* Video Player Modal */}
              <Dialog open={!!playingVideo} onOpenChange={() => setPlayingVideo(null)}>
                <DialogContent className="max-w-4xl bg-[#0a0a0b] border-gray-800 p-0 overflow-hidden">
                  <DialogHeader className="p-4 border-b border-gray-800 flex flex-row items-center justify-between space-y-0">
                    <DialogTitle className="text-white font-black uppercase tracking-tighter text-xl italic">
                      {playingVideo?.title}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="bg-black">
                    {playingVideo && (
                      <VideoPlayer
                        videoUrl={playingVideo.video_url || playingVideo.url ?
                          (playingVideo.video_url || playingVideo.url).startsWith('http') ?
                            (playingVideo.video_url || playingVideo.url) :
                            `${BACKEND_URL}${playingVideo.video_url || playingVideo.url}`
                          : null}
                        videoId={playingVideo.id}
                        teamId={selectedPlayer.team_id}
                      />
                    )}
                  </div>
                </DialogContent>
              </Dialog>

              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-black text-gray-500 uppercase tracking-widest">{t('playerStats.growthCurves')}</h3>
                <div className="flex bg-[#1a1a1b] rounded-lg p-1 border border-gray-800">
                  <button onClick={() => setTimeFilter('month')} className={`px-2 py-0.5 text-[10px] font-bold rounded-md transition-colors ${timeFilter === 'month' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-white'}`}>{t('playerStats.last30Days').replace('Last ', '')}</button>
                  <button onClick={() => setTimeFilter('3months')} className={`px-2 py-0.5 text-[10px] font-bold rounded-md transition-colors ${timeFilter === '3months' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-white'}`}>{t('playerStats.last3Months').replace('Last ', '')}</button>
                  <button onClick={() => setTimeFilter('season')} className={`px-2 py-0.5 text-[10px] font-bold rounded-md transition-colors ${timeFilter === 'season' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-white'}`}>{t('playerStats.thisSeason').replace('This ', '')}</button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {Object.keys(selectedPlayer.historyMap).length === 0 ? (
                  <div className="col-span-full py-20 bg-[#1a1a1b] rounded-2xl border border-dashed border-gray-800 flex flex-col items-center justify-center text-center">
                    <BarChart3 className="w-12 h-12 text-gray-800 mb-4" />
                    <p className="text-gray-500 font-medium">{t('playerStats.insufficientData')}</p>
                  </div>
                ) : (
                  Object.entries(selectedPlayer.historyMap).map(([statType, playerHistory]) => {
                    const peerCurveMap = calculatePeerAverageCurve(statType, getPlayerCategory(selectedPlayer), globalHistoryMaps);

                    // Merge peer average into player history for the chart
                    const chartData = playerHistory.map(h => {
                      const dateKey = format(h.timestamp, 'yyyy-MM-dd');
                      return {
                        ...h,
                        peerAverage: peerCurveMap[dateKey] || 0
                      };
                    });

                    return (
                      <Card key={statType} className="bg-[#1a1a1b] border-gray-800 overflow-hidden group">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-xs font-black text-gray-400 uppercase tracking-widest group-hover:text-blue-400 transition-colors">
                              {t('playerStats.progress', { stat: formatStatName(statType, t) })}
                            </CardTitle>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setSelectedStatType(selectedStatType === statType ? null : statType)}
                              className={`h-7 px-2 text-[10px] font-bold transition-all ${selectedStatType === statType
                                ? 'bg-snapp-green/20 text-snapp-green border border-snapp-green/30'
                                : 'text-gray-500 hover:text-snapp-green hover:bg-snapp-green/10'
                                }`}
                            >
                              <BarChart3 className="w-3 h-3 mr-1" />
                              {t('playerStats.videos')}
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="h-[250px] pt-4">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
                              <XAxis
                                dataKey="date"
                                stroke="#525252"
                                fontSize={10}
                                fontWeight="bold"
                                tickLine={false}
                                axisLine={false}
                              />
                              <YAxis
                                stroke="#525252"
                                fontSize={10}
                                fontWeight="bold"
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={(value) => `+${value}`}
                              />
                              <Tooltip
                                contentStyle={{ backgroundColor: '#1a1a1b', border: '1px solid #3f3f46', borderRadius: '12px' }}
                                itemStyle={{ color: '#60a5fa', fontWeight: 'bold', fontSize: '12px' }}
                                labelStyle={{ color: '#94a3b8', fontWeight: '800', marginBottom: '4px', fontSize: '10px', textTransform: 'uppercase' }}
                              />
                              <Line
                                type="monotone"
                                dataKey="value"
                                stroke="#3b82f6"
                                strokeWidth={3}
                                dot={{ r: 4, fill: '#3b82f6', strokeWidth: 2, stroke: '#1a1a1b' }}
                                activeDot={{ r: 6, fill: '#60a5fa' }}
                                name={t('playerStats.sessionPerformance')}
                              />

                              <Line
                                type="monotone"
                                dataKey="peerAverage"
                                stroke="#9ca3af" // Gray
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                                name={t('playerStats.peerAverage')}
                                opacity={0.8}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    )
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </TeamGate>
  );
};

export default PlayerStats;
